import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import ProductsAPI from 'dashboard/api/products';
import ProductGroupsAPI from 'dashboard/api/productGroups';

// camelCase só no primeiro nível: as chaves de custom_attributes são dados do cliente.
const toProduct = ({ custom_attributes: customAttributes, ...product }) => ({
  ...camelcaseKeys(product),
  customAttributes: customAttributes || {},
});

export const useProductsStore = defineStore('products', {
  state: () => ({
    records: [],
    groups: [],
    meta: { totalCount: 0, currentPage: 1, perPage: 25, units: [] },
    uiFlags: { fetching: false, saving: false },
  }),

  actions: {
    async fetch({ page = 1, q = '', productGroupId = '' } = {}) {
      this.uiFlags.fetching = true;
      try {
        const { data } = await ProductsAPI.list({
          page,
          q: q || undefined,
          product_group_id: productGroupId || undefined,
        });
        this.records = (data.payload || []).map(toProduct);
        this.meta = camelcaseKeys(data.meta || {});
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.uiFlags.fetching = false;
      }
    },

    // Campos em JSON (mantém os tipos dos campos personalizados); a imagem vai à parte.
    async save(id, payload) {
      this.uiFlags.saving = true;
      try {
        const { data } = id
          ? await ProductsAPI.update(id, payload)
          : await ProductsAPI.create(payload);
        return toProduct(data.payload);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.saving = false;
      }
    },

    async uploadImage(id, file) {
      const formData = new FormData();
      formData.append('product[image]', file);
      try {
        const { data } = await ProductsAPI.update(id, formData);
        return toProduct(data.payload);
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async remove(id) {
      try {
        await ProductsAPI.delete(id);
        this.records = this.records.filter(product => product.id !== id);
        this.meta.totalCount = Math.max(this.meta.totalCount - 1, 0);
      } catch (error) {
        throwErrorMessage(error);
      }
    },

    async fetchGroups() {
      try {
        const { data } = await ProductGroupsAPI.get();
        this.groups = data.payload || [];
      } catch (error) {
        throwErrorMessage(error);
      }
    },

    async createGroup(name) {
      try {
        const { data } = await ProductGroupsAPI.create({
          product_group: { name },
        });
        this.groups = [...this.groups, data.payload].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        return data.payload;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },
  },
});
